import { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isEmpty, isNil } from 'lodash';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Import UI Components
import { BusyLoader } from '@geneui/components';
// Import Components
import GeneralInfo from './GeneralInfo';
import CMSBannerSection from './CMSBannerSection';
import TemplateInfo from './TemplateInfo';
import PushNotificationInfoDetails from './PushNotificationInfoDetails';
import TemplateActionsButtons from 'components/TemplateList/TemplateActionsButtons';
// Import Services
import { TemplateHttpService } from 'services/http';
// Import Hooks
import useGoToRoute from 'hooks/GoToRoute';
import useToaster from 'hooks/UseToaster';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useQueryParams from 'hooks/UseQueryParams';
// Import Config
import { modalsStateSetDictKeys } from 'pages/CampaignListPage/config';
// Import Constants
import { TemplateTypesLabels, l, AlertTypes } from 'constants/common';
import { RoutesList } from 'routes';
// Import Context
import { useVersionHistoryContext } from 'components/VersionHistory';
// Import Styles
import 'assets/scss/templateInfoModal.scss';
import styles from './templateModal.module.scss';

const { warning } = AlertTypes;

const {
    TEMPLATE_CLONE_EMAIL,
    TEMPLATE_CLONE_SMS,
    TEMPLATE_CLONE_INTERNAL_MESSAGE,
    TEMPLATE_CLONE_SOCIAL_TELEGRAM,
    TEMPLATE_CLONE_PUSH_NOTIFICATION,
    TEMPLATE_CLONE_POP_UP_NOTIFICATION,
    TEMPLATE_CLONE_DYNAMIC_POP_UP,
    TEMPLATE_CLONE_CMS_POP_UP_NOTIFICATION,
    TEMPLATE_CLONE_CMS_BANNER,
    TEMPLATE_EDIT_EMAIL,
    TEMPLATE_EDIT_SMS,
    TEMPLATE_EDIT_INTERNAL_MESSAGE,
    TEMPLATE_EDIT_SOCIAL_TELEGRAM,
    TEMPLATE_EDIT_PUSH_NOTIFICATION,
    TEMPLATE_EDIT_POP_UP_NOTIFICATION,
    TEMPLATE_EDIT_DYNAMIC_POP_UP,
    TEMPLATE_EDIT_CMS_POP_UP_NOTIFICATION,
    TEMPLATE_EDIT_CMS_BANNER,
    TEMPLATE_CLONE_SOCIAL_VIBER,
    TEMPLATE_CLONE_HOORY,
    TEMPLATE_CLONE_SOCIAL_WHATS_APP,
    TEMPLATE_EDIT_SOCIAL_VIBER,
    TEMPLATE_EDIT_HOORY,
    TEMPLATE_EDIT_SOCIAL_WHATS_APP,
} = RoutesList;

const TemplateInfoModalBody = (props) => {
    const {
        isLoading,
        isPredefined,
        templateInfo,
        templateId,
        childItemsInfo,
        getTemplateInfo,
        withActionButtons,
        handleInfoModalActionClick,
        templateProviders,
        predefinedKeyValue,
    } = props;
    const { t } = useTranslation();
    const { search } = useLocation();
    const { getRoutePath, goToPath } = useGoToRoute();
    const { showToaster } = useToaster();
    const { doGetRequest } = useRequest();
    const goToRouteQueryParams = useQueryParams();

    const { pointInTime } = useVersionHistoryContext();

    const isCMSBanner = templateInfo?.DeliveryMethod === TemplateTypesLabels.CMSBanner;

    const { getCampaignsByTemplateRequest } = useCancelableRequests({
        getCampaignsByTemplateRequest: TemplateHttpService.getCampaignsByTemplate,
    });

    const additional = useMemo(() => {
        if (
            !isNil(templateInfo?.DeliveryMethod) &&
            [TemplateTypesLabels.PopUp, TemplateTypesLabels.CMSPopUp, TemplateTypesLabels.DynamicPopUp].includes(
                templateInfo.DeliveryMethod,
            )
        ) {
            return JSON.parse(templateInfo.Body);
        }

        return null;
    }, [templateInfo]);

    const pushNotificationBodyData = useMemo(() => {
        if (
            !isNil(templateInfo?.DeliveryMethod) &&
            templateInfo.DeliveryMethod === TemplateTypesLabels.PushNotification &&
            templateInfo.Body
        ) {
            try {
                const parsed = JSON.parse(templateInfo.Body);
                return {
                    appType: parsed.app_type,
                    deviceTypes: parsed.session_source,
                    routeId: parsed.extra_info?.notification_routes,
                };
            } catch {
                return null;
            }
        }

        return null;
    }, [templateInfo]);

    const generalInfo = (
        <GeneralInfo
            isPredefined={isPredefined}
            name={templateInfo?.Name}
            description={templateInfo?.Description}
            type={templateInfo?.DeliveryMethod}
            templateId={templateInfo?.TemplateId}
            additional={additional}
            templateCategoryIds={templateInfo?.TemplateCategoryIds}
            predefinedKeyValue={predefinedKeyValue}
        />
    );

    // Actions part
    const deleteTemplateAction = (template) => {
        handleInfoModalActionClick(modalsStateSetDictKeys.deleteTemplate, template);
    };

    const archiveTemplateAction = (template) => {
        handleInfoModalActionClick(modalsStateSetDictKeys.archiveTemplate, template);
    };

    const unarchiveTemplateAction = (template) => {
        handleInfoModalActionClick(modalsStateSetDictKeys.unarchiveTemplate, template);
    };

    const testTemplateAction = (template) => {
        if (
            (template.DeliveryMethod === TemplateTypesLabels.Email ||
                template.DeliveryMethod === TemplateTypesLabels.SMS) &&
            isEmpty(templateProviders[template.DeliveryMethod])
        ) {
            showToaster(warning, t(l.ProviderIsNotIntegrated));
        } else {
            handleInfoModalActionClick(modalsStateSetDictKeys.testTemplate, template);
        }
    };

    const getEditTemplatePath = (template) => {
        const { DeliveryMethod, TemplateId } = template;
        const params = { templateId: TemplateId };
        let path;

        switch (DeliveryMethod) {
            case TemplateTypesLabels.Email:
                path = TEMPLATE_EDIT_EMAIL;
                break;
            case TemplateTypesLabels.SMS:
                path = TEMPLATE_EDIT_SMS;
                break;
            case TemplateTypesLabels.Internal:
                path = TEMPLATE_EDIT_INTERNAL_MESSAGE;
                break;
            case TemplateTypesLabels.Telegram:
                path = TEMPLATE_EDIT_SOCIAL_TELEGRAM;
                break;
            case TemplateTypesLabels.PushNotification:
                path = TEMPLATE_EDIT_PUSH_NOTIFICATION;
                break;
            case TemplateTypesLabels.PopUp:
                path = TEMPLATE_EDIT_POP_UP_NOTIFICATION;
                break;
            case TemplateTypesLabels.DynamicPopUp:
                path = TEMPLATE_EDIT_DYNAMIC_POP_UP;
                break;
            case TemplateTypesLabels.CMSPopUp:
                path = TEMPLATE_EDIT_CMS_POP_UP_NOTIFICATION;
                break;
            case TemplateTypesLabels.CMSBanner:
                path = TEMPLATE_EDIT_CMS_BANNER;
                break;
            case TemplateTypesLabels.Viber:
                path = TEMPLATE_EDIT_SOCIAL_VIBER;
                break;
            case TemplateTypesLabels.Hoory:
                path = TEMPLATE_EDIT_HOORY;
                break;
            case TemplateTypesLabels.WhatsApp:
                path = TEMPLATE_EDIT_SOCIAL_WHATS_APP;
                break;
            default:
        }

        return getRoutePath(path, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const goToEditTemplate = (template) => {
        const path = getEditTemplatePath(template);
        goToPath(path);
    };

    const editTemplateAction = (template) => {
        doGetRequest(getCampaignsByTemplateRequest.request, {
            queryString: { templateId: template.TemplateId, pointInTime },
            successCallback: (Data) => {
                if (isEmpty(Data)) {
                    goToEditTemplate(template);
                } else {
                    handleInfoModalActionClick(modalsStateSetDictKeys.editTemplate, {
                        objects: Data.map(({ Name, Type }) => ({ Name, Type })),
                        template,
                    });
                }
            },
        });
    };

    const getCloneTemplatePath = (template) => {
        const { DeliveryMethod, TemplateId } = template;
        const params = { templateId: TemplateId };

        switch (DeliveryMethod) {
            case TemplateTypesLabels.Email:
                return getRoutePath(TEMPLATE_CLONE_EMAIL, params);
            case TemplateTypesLabels.SMS:
                return getRoutePath(TEMPLATE_CLONE_SMS, params);
            case TemplateTypesLabels.Internal:
                return getRoutePath(TEMPLATE_CLONE_INTERNAL_MESSAGE, params);
            case TemplateTypesLabels.Telegram:
                return getRoutePath(TEMPLATE_CLONE_SOCIAL_TELEGRAM, params);
            case TemplateTypesLabels.PushNotification:
                return getRoutePath(TEMPLATE_CLONE_PUSH_NOTIFICATION, params);
            case TemplateTypesLabels.PopUp:
                return getRoutePath(TEMPLATE_CLONE_POP_UP_NOTIFICATION, params);
            case TemplateTypesLabels.DynamicPopUp:
                return getRoutePath(TEMPLATE_CLONE_DYNAMIC_POP_UP, params);
            case TemplateTypesLabels.CMSPopUp:
                return getRoutePath(TEMPLATE_CLONE_CMS_POP_UP_NOTIFICATION, params);
            case TemplateTypesLabels.CMSBanner:
                return getRoutePath(TEMPLATE_CLONE_CMS_BANNER, params);
            case TemplateTypesLabels.Viber:
                return getRoutePath(TEMPLATE_CLONE_SOCIAL_VIBER, params);
            case TemplateTypesLabels.Hoory:
                return getRoutePath(TEMPLATE_CLONE_HOORY, params);
            case TemplateTypesLabels.WhatsApp:
                return getRoutePath(TEMPLATE_CLONE_SOCIAL_WHATS_APP, params);
            default:
        }
    };

    return (
        <BusyLoader isBusy={isLoading} type="spinner" spinnerSize="medium">
            {!isNil(templateInfo) && !isLoading && (
                <div className={classNames(styles.container)}>
                    <div className="crm-scrollable-container">
                        {withActionButtons && (
                            <div className="crm-info-template-action-buttons-container">
                                <TemplateActionsButtons
                                    data={templateInfo}
                                    isInfoVisible={false}
                                    isViewMode={false}
                                    testTemplateAction={testTemplateAction}
                                    unarchiveTemplateAction={unarchiveTemplateAction}
                                    archiveTemplateAction={archiveTemplateAction}
                                    deleteTemplateAction={deleteTemplateAction}
                                    getCloneTemplatePath={getCloneTemplatePath}
                                    editTemplateAction={editTemplateAction}
                                    getEditTemplatePath={getEditTemplatePath}
                                    handleActionClick={handleInfoModalActionClick}
                                />
                            </div>
                        )}
                        {isCMSBanner ? (
                            <CMSBannerSection body={templateInfo?.Body} generalInfo={generalInfo} />
                        ) : (
                            generalInfo
                        )}

                        {templateInfo?.DeliveryMethod !== TemplateTypesLabels.PopUp &&
                            templateInfo?.DeliveryMethod !== TemplateTypesLabels.CMSPopUp &&
                            !isCMSBanner && (
                                <div className="crm-object-modal-bottom smb-wrap">
                                    <div className="title-wrapper">
                                        <p className="title-wrapper-text">{t(l.TemplateInfo)}</p>
                                        <span className="title-wrapper-line" />
                                    </div>
                                    {pushNotificationBodyData && (
                                        <div className="crm-object-modal-head">
                                            <PushNotificationInfoDetails
                                                appType={pushNotificationBodyData.appType}
                                                deviceTypes={pushNotificationBodyData.deviceTypes}
                                                routeId={pushNotificationBodyData.routeId}
                                            />
                                        </div>
                                    )}
                                    <TemplateInfo
                                        defaultTemplateId={templateId}
                                        deliveryMethod={templateInfo?.DeliveryMethod}
                                        defaultBody={templateInfo?.Body}
                                        defaultSubject={templateInfo?.Subject}
                                        childItemsInfo={childItemsInfo}
                                        getTemplateInfo={getTemplateInfo}
                                    />
                                </div>
                            )}
                    </div>
                </div>
            )}
        </BusyLoader>
    );
};

TemplateInfoModalBody.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    isPredefined: PropTypes.bool,
    templateInfo: PropTypes.object,
    templateId: PropTypes.number,
    childItemsInfo: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    getTemplateInfo: PropTypes.func.isRequired,
    withActionButtons: PropTypes.bool,
    templateProviders: PropTypes.object,
    handleInfoModalActionClick: PropTypes.func,
    predefinedKeyValue: PropTypes.object,
};

TemplateInfoModalBody.defaultProps = {
    templateInfo: null,
    withActionButtons: false,
    childItemsInfo: [],
};

export default TemplateInfoModalBody;
